import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getConfig } from '../../config/runtimeConfig';
import {
  ApiError, CODE_RE, kindOfSegment, redeemShare, shareGet, sharePath, uidSuffix, type ShareKind,
} from '../../features/share/api';
import { useSession } from '../layout/TopBar';
import { routeBase } from '../api/collections';
import type { PublicCollection } from '../api/explore';
import { CollectionView } from '../pages/PublicCollectionPage';
import { CommunityStudyView, type StudyHead } from '../pages/StudyPageCommunity';
import { NotAvailable } from './NotAvailable';
import { inPageOrder } from '../pages/studyNeighbours';
import type { Siblings } from '../pages/StudyNav';

/**
 * Opening a share link (decided 2026-09-15):
 *
 *   /s/col/<code>                         the collection's page
 *   /s/col/<code>/<study>[/<slide>]       a study opened inside it (the address
 *                                         while browsing; nobody copies this)
 *   /s/stu/<code>[/<slide>]               the study, on a slide
 *
 * The code is redeemed first. What happens next depends on the item's own
 * setting and on who is holding the link:
 *
 *   Shared   → a member goes to the ordinary page; anyone else opens it here,
 *              through the link, signed in or not.
 *   Public   → the ordinary page: browsing is open, the viewer needs an account.
 *   Private  → paused. A member goes to the ordinary page; anyone else is told
 *              the link is not available.
 *
 * The kind segment and the suffixes are labels: a kind that does not match
 * the code answers "not available", and a suffix only chooses among what the
 * link already reaches.
 */
const normalPath = (kind: ShareKind, id: string) =>
  kind === 'collection' ? `${routeBase()}/collections/${id}` : `${routeBase()}/studies/${encodeURIComponent(id)}`;

async function readsOwn(path: string): Promise<boolean> {
  const res = await fetch(`${getConfig().apiRoot}${path}`, { credentials: 'include' });
  return res.ok;
}

type Entry =
  | { state: 'loading' }
  | { state: 'unavailable'; signinTo?: string; busy?: boolean }
  | { state: 'go'; to: string }
  | { state: 'open'; shareId: string; targetId: string };

function useShareEntry(kind: ShareKind | null, code: string | undefined): Entry {
  const valid = !!kind && !!code && CODE_RE.test(code);
  const { data: me, isLoading } = useSession();
  const signedIn = isLoading ? undefined : !!me?.userId;
  const redeem = useQuery({
    queryKey: ['share-redeem', code],
    queryFn: () => redeemShare(code!),
    enabled: valid,
    retry: false,
    staleTime: 60_000,
  });
  const r = redeem.data;
  const matches = !!r && r.kind === kind;
  // Somebody who can read the item anyway (a member) gets the ordinary page,
  // with everything a member can do there.
  const ownPath = r ? (r.kind === 'collection' ? `/collections/${r.targetId}?limit=1` : `/studies/${encodeURIComponent(r.targetId)}`) : '';
  const own = useQuery({
    queryKey: ['share-own', r?.kind, r?.targetId],
    queryFn: () => readsOwn(ownPath),
    enabled: matches && r?.access === 'link' && signedIn === true,
    retry: false,
  });
  if (!valid) return { state: 'unavailable' };
  if (redeem.isLoading || signedIn === undefined) return { state: 'loading' };
  // Too many failed codes from this address: say so, rather than blame the link.
  if (redeem.error instanceof ApiError && redeem.error.status === 429) return { state: 'unavailable', busy: true };
  if (!r || !matches) return { state: 'unavailable' };
  const normal = normalPath(r.kind, r.targetId);
  if (r.access === 'public') return { state: 'go', to: normal };
  if (r.access === 'private') return signedIn ? { state: 'go', to: normal } : { state: 'unavailable', signinTo: normal };
  if (signedIn) {
    if (own.isLoading) return { state: 'loading' };
    if (own.data) return { state: 'go', to: normal };
  }
  return r.shareId ? { state: 'open', shareId: r.shareId, targetId: r.targetId } : { state: 'unavailable' };
}

const BUSY = { title: 'Too many attempts', detail: 'Too many links failed to open from here. Try again in a minute.' };

/** A study's cover inside the link, by public id. */
const shareThumb = (shareId: string, studyId: string) =>
  `${getConfig().apiRoot}/s/${encodeURIComponent(shareId)}/studies/${encodeURIComponent(studyId)}/thumbnail`;

/** `/s/col/<code>`: the shared collection's page, inside the shell. */
export function ShareCollectionRoute() {
  const { code } = useParams<{ code: string }>();
  const nav = useNavigate();
  const { data: me } = useSession();
  const entry = useShareEntry('collection', code);
  const shareId = entry.state === 'open' ? entry.shareId : undefined;
  const col = useQuery({
    queryKey: ['share-collection', shareId],
    queryFn: () => shareGet<PublicCollection>(shareId!, '/collection'),
    enabled: !!shareId,
  });
  if (entry.state === 'go') return <Navigate replace to={entry.to} />;
  if (entry.state === 'unavailable') return <NotAvailable inShell signinTo={entry.signinTo} {...(entry.busy ? BUSY : {})} />;
  if (entry.state === 'loading' || col.isLoading) return <div style={{ flex: 1 }} />;
  if (!col.data || !shareId) return <NotAvailable inShell />;
  return (
    <CollectionView
      c={col.data}
      guest={!me?.userId}
      shareId={shareId}
      thumb={(id) => shareThumb(shareId, id)}
      onOpenStudy={(id) => nav(sharePath('collection', code!, uidSuffix(id)))}
    />
  );
}

/** `/s/stu/<code>[/<slide>]` and `/s/col/<code>/<study>[/<slide>]`: the viewer, through the link. */
export function ShareStudyRoute() {
  const { seg, code, '*': rest = '' } = useParams<{ seg: string; code: string; '*': string }>();
  const kind = kindOfSegment(seg);
  const nav = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const parts = rest.split('/').filter(Boolean);
  const inCollection = kind === 'collection';
  const shapeOk = inCollection ? parts.length >= 1 && parts.length <= 2 : parts.length <= 1;
  const entry = useShareEntry(shapeOk ? kind : null, code);
  const shareId = entry.state === 'open' ? entry.shareId : undefined;
  const col = useQuery({
    queryKey: ['share-collection', shareId],
    queryFn: () => shareGet<PublicCollection>(shareId!, '/collection'),
    enabled: !!shareId && inCollection,
  });
  // Resolved against what the link already reaches: the studies of the shared
  // collection, or the one study a study link is for. Public ids throughout.
  const studyId = entry.state !== 'open'
    ? undefined
    : inCollection
      ? col.data?.studies.find((s) => uidSuffix(s.public_id) === parts[0])?.public_id
      : entry.targetId;
  const slide = inCollection ? parts[1] : parts[0];
  const head = useQuery({
    queryKey: ['share-study-head', shareId, studyId],
    queryFn: () => shareGet<StudyHead>(shareId!, `/studies/${encodeURIComponent(studyId!)}`),
    enabled: !!shareId && !!studyId,
  });
  // Previous / next inside a shared collection: its studies as its page lists
  // them, each addressed through the link, its head fetched through it too.
  const siblings = useMemo<Siblings | undefined>(() => {
    const c = col.data;
    if (!inCollection || !c || !shareId) return undefined;
    return {
      order: inPageOrder(c.groups, c.studies),
      hrefOf: (id) => sharePath('collection', code!, uidSuffix(id)),
      prefetch: (id) => void qc.prefetchQuery({ queryKey: ['share-study-head', shareId, id], queryFn: () => shareGet<StudyHead>(shareId, `/studies/${encodeURIComponent(id)}`) }),
    };
  }, [inCollection, col.data, shareId, code, qc]);
  if (entry.state === 'go') return <Navigate replace to={entry.to} />;
  if (entry.state === 'unavailable') return <NotAvailable signinTo={entry.signinTo} {...(entry.busy ? BUSY : {})} />;
  if (entry.state === 'loading' || (inCollection && col.isLoading) || (!!studyId && head.isLoading)) {
    return <div style={{ height: '100vh', background: 'var(--tr-page)' }} />;
  }
  if (!studyId || !head.data || !shareId) return <NotAvailable />;
  // The slide on screen lives in the path, so the address bar always names it.
  const onSlide = (seriesUid: string) => {
    const to = inCollection
      ? sharePath('collection', code!, parts[0], uidSuffix(seriesUid))
      : sharePath('study', code!, uidSuffix(seriesUid));
    // The query string stays: StudyPage keeps compare mode there
    // (?compare&a&b&active…), read at call time so it is never an older copy.
    if (to !== location.pathname) nav({ pathname: to, search: window.location.search }, { replace: true });
  };
  // Keyed by study, as the app's route is: a step to a neighbour mounts a fresh viewer.
  return (
    <CommunityStudyView
      key={studyId}
      head={head.data}
      share={{ shareId, kind: kind!, collectionPath: inCollection ? sharePath('collection', code!) : undefined }}
      slide={slide}
      onSlide={onSlide}
      siblings={siblings}
    />
  );
}
